import EntityProfilePic from "@/components/EntityProfilePic";
import Markdown from "@/components/Markdown";
import MathText from "@/components/MathText";
import OrcidId from "@/components/OrcidId";
import PaperAssessment from "@/components/PaperAssessment";
import PaperBookmarkButton from "@/components/PaperBookmarkButton";
import { LinkExternal01Icon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { useAuth } from "@/utils/auth";
import { cn } from "@/utils/tailwind";
import { ReactNode, Suspense, useEffect } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { graphql, useFragment, useMutation } from "react-relay";
import { Link } from "react-router";
import { PaperDetailFragment$key } from "./__generated__/PaperDetailFragment.graphql";
import { PaperDetailViewMutation } from "./__generated__/PaperDetailViewMutation.graphql";

const PaperDetailFragment = graphql`
  fragment PaperDetailFragment on Paper {
    id
    arxivId
    openalexId
    version
    title
    abstract
    authors
    primaryCategory
    categories
    publishedAt
    announcedOn
    updatedAt
    doi
    journalRef
    comment
    venue
    citedByCount
    absUrl
    pdfUrl
    pdfLicense
    htmlUrl
    landingUrl
    linkedAuthors {
      id
      username
      displayName
      orcid
      ...EntityProfilePicFragment @arguments(thumbnail: true)
    }
    versions {
      number
      title
      abstract
      authors
      comment
      submittedAt
      absUrl
      pdfUrl
      htmlUrl
    }
    ...PaperBookmarkButtonFragment
    ...PaperAssessmentFragment
  }
`;

const ViewMutation = graphql`
  mutation PaperDetailViewMutation($id: ID!) {
    viewPaper(id: $id) {
      id
    }
  }
`;

/** Counts the viewer, once per page load: distinct viewers lift a paper in
 * the trending order, and the viewer's For You recommends papers like it. */
function useRecordView(id: string) {
  const { isAuthenticated } = useAuth();
  const [view] = useMutation<PaperDetailViewMutation>(ViewMutation);
  useEffect(() => {
    if (isAuthenticated) view({ variables: { id } });
  }, [id, isAuthenticated, view]);
}

export interface PaperDetailProps {
  paper: PaperDetailFragment$key;
  /** The arXiv version to show; the latest when unset. */
  version?: number;
}

export default function PaperDetail(props: PaperDetailProps) {
  const intl = useIntl();
  const paper = useFragment(PaperDetailFragment, props.paper);
  useRecordView(paper.id);
  // An earlier version; the paper itself is its latest one.
  const earlier =
    props.version !== undefined && props.version < (paper.version ?? 0)
      ? paper.versions.find((version) => version.number === props.version)
      : undefined;
  const shown = earlier ?? paper;
  const shownVersion = earlier?.number ?? paper.version;
  const revisedAt = earlier ? earlier.submittedAt : paper.updatedAt;
  const revised = (shownVersion ?? 0) > 1 && revisedAt;
  const source = [paper.arxivId && `arXiv:${paper.arxivId}`, paper.venue]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className="flex flex-col gap-6 pb-16">
      <header className="flex flex-col gap-3">
        <p className="text-brand-500 text-sm font-semibold">{source}</p>
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-grey-900 text-3xl font-semibold leading-10">
            <MathText>{shown.title}</MathText>
          </h1>
          <PaperBookmarkButton paper={paper} labelled />
        </div>
        <p className="text-grey-700">{shown.authors.join(", ")}</p>
        {paper.linkedAuthors.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <span className="text-grey-500">
              <FormattedMessage defaultMessage="On Aqora:" />
            </span>
            {paper.linkedAuthors.map((author) => (
              <span
                key={author.id}
                className="inline-flex items-center gap-1.5"
              >
                <Link
                  to={`/${author.username}`}
                  className="inline-flex items-center gap-1.5 text-grey-900 hover:text-brand-500"
                >
                  <EntityProfilePic entity={author} size="5" />
                  {author.displayName}
                </Link>
                {author.orcid && (
                  <OrcidId orcid={author.orcid} variant="icon" />
                )}
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-grey-500 text-sm">
          {paper.categories.map((category) => (
            <Badge
              key={category}
              size="sm"
              variant={
                category === paper.primaryCategory ? "secondary" : "default"
              }
            >
              {category}
            </Badge>
          ))}
          <span>
            {/* Papers from arXiv's listing are dated by first submission;
                the rest by publication. */}
            {paper.primaryCategory ? (
              <FormattedMessage
                defaultMessage="Submitted {date}"
                values={{
                  date: (
                    <FormattedDate
                      dateStyle="medium"
                      value={paper.publishedAt}
                    />
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Published {date}"
                values={{
                  date: (
                    // A date without a time, held as midnight UTC.
                    <FormattedDate
                      dateStyle="medium"
                      value={paper.publishedAt}
                      timeZone="UTC"
                    />
                  ),
                }}
              />
            )}
          </span>
          {paper.primaryCategory && (
            <span>
              <FormattedMessage
                defaultMessage="• Announced {date}"
                values={{
                  date: (
                    <FormattedDate
                      dateStyle="medium"
                      value={paper.announcedOn}
                      timeZone="UTC"
                    />
                  ),
                }}
              />
            </span>
          )}
          {paper.citedByCount != null && (
            <span>
              <FormattedMessage
                defaultMessage="• {count, plural, one {# citation} other {# citations}}"
                values={{ count: paper.citedByCount }}
              />
            </span>
          )}
          {revised && (
            <span>
              <FormattedMessage
                defaultMessage="• Revised {date} (v{version})"
                values={{
                  date: (
                    <FormattedDate
                      dateStyle="medium"
                      value={revisedAt ?? undefined}
                    />
                  ),
                  version: shownVersion,
                }}
              />
            </span>
          )}
        </div>
        {paper.arxivId && paper.versions.length > 1 && (
          <nav
            aria-label={intl.formatMessage({ defaultMessage: "Versions" })}
            className="flex flex-wrap gap-2 text-sm"
          >
            {paper.versions.map((version) => {
              const current = version.number === shownVersion;
              return (
                <Link
                  key={version.number}
                  to={
                    version.number === paper.version
                      ? `/papers/${paper.arxivId}`
                      : `/papers/${paper.arxivId}v${version.number}`
                  }
                  aria-current={current ? "page" : undefined}
                  className={cn(
                    "rounded-lg border px-2.5 py-1",
                    current
                      ? "border-brand-500 bg-brand-50 text-brand-700 font-semibold"
                      : "border-grey-300 text-grey-700 hover:bg-grey-50",
                  )}
                >
                  <FormattedMessage
                    defaultMessage="v{version} · {date}"
                    values={{
                      version: version.number,
                      date: (
                        <FormattedDate
                          dateStyle="medium"
                          value={version.submittedAt}
                        />
                      ),
                    }}
                  />
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      <div className="flex flex-wrap gap-3">
        {shown.absUrl && (
          <ExternalLink href={shown.absUrl}>
            <FormattedMessage defaultMessage="arXiv" />
          </ExternalLink>
        )}
        {shown.htmlUrl && (
          <ExternalLink href={shown.htmlUrl}>
            <FormattedMessage defaultMessage="HTML" />
          </ExternalLink>
        )}
        {shown.pdfUrl && (
          <ExternalLink href={shown.pdfUrl}>
            {paper.pdfLicense ? (
              <FormattedMessage
                defaultMessage="PDF ({license})"
                values={{ license: paper.pdfLicense }}
              />
            ) : (
              <FormattedMessage defaultMessage="PDF" />
            )}
          </ExternalLink>
        )}
        {!paper.arxivId && paper.landingUrl && (
          <ExternalLink href={paper.landingUrl}>
            {paper.doi ? (
              <FormattedMessage defaultMessage="Publisher" />
            ) : (
              <FormattedMessage defaultMessage="OpenAlex" />
            )}
          </ExternalLink>
        )}
      </div>

      {shown.abstract && (
        <section className="flex flex-col gap-2">
          <h2 className="text-grey-900 text-xl font-semibold">
            <FormattedMessage defaultMessage="Abstract" />
          </h2>
          <Suspense
            fallback={<p className="whitespace-pre-line">{shown.abstract}</p>}
          >
            <Markdown className="text-grey-700">{shown.abstract}</Markdown>
          </Suspense>
        </section>
      )}

      <PaperAssessment paper={paper} viewedVersion={earlier?.number} />

      {(paper.journalRef || paper.doi || shown.comment) && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          {paper.journalRef && (
            <>
              <dt className="text-grey-500">
                <FormattedMessage defaultMessage="Journal reference" />
              </dt>
              <dd className="text-grey-700">{paper.journalRef}</dd>
            </>
          )}
          {paper.doi && (
            <>
              <dt className="text-grey-500">
                <FormattedMessage defaultMessage="DOI" />
              </dt>
              <dd>
                <a
                  href={`https://doi.org/${paper.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-500 hover:underline"
                >
                  {paper.doi}
                </a>
              </dd>
            </>
          )}
          {shown.comment && (
            <>
              <dt className="text-grey-500">
                <FormattedMessage defaultMessage="Comments" />
              </dt>
              <dd className="text-grey-700">{shown.comment}</dd>
            </>
          )}
        </dl>
      )}
    </article>
  );
}

function ExternalLink(props: { href: string; children: ReactNode }) {
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-lg border border-grey-300 bg-white px-3 py-2 text-sm font-semibold text-grey-700 shadow-xs hover:bg-grey-50"
    >
      {props.children}
      <LinkExternal01Icon className="w-4 h-4" />
    </a>
  );
}
